import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Select, 
  DatePicker, 
  Button, 
  Space,
  Statistic,
  Table
} from 'antd';
import { 
  BarChartOutlined, 
  LineChartOutlined, 
  PieChartOutlined,
  DownloadOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from 'recharts';
import { mockUsers, mockProfitRecords, roleMap } from '../services/mockData';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

const DataAnalytics: React.FC = () => {
  const [timeRange, setTimeRange] = useState('month');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [loading, setLoading] = useState(false);

  // 模拟收益趋势数据
  const revenueTrendData = [
    { month: '1月', revenue: 1200000, profit: 240000, users: 120 },
    { month: '2月', revenue: 1350000, profit: 270000, users: 135 },
    { month: '3月', revenue: 1180000, profit: 236000, users: 142 },
    { month: '4月', revenue: 1420000, profit: 284000, users: 156 },
    { month: '5月', revenue: 1680000, profit: 336000, users: 168 },
    { month: '6月', revenue: 1850000, profit: 370000, users: 185 },
    { month: '7月', revenue: 1920000, profit: 384000, users: 192 },
    { month: '8月', revenue: 2100000, profit: 420000, users: 210 },
    { month: '9月', revenue: 2280000, profit: 456000, users: 228 },
    { month: '10月', revenue: 2350000, profit: 470000, users: 235 },
    { month: '11月', revenue: 2380000, profit: 476000, users: 238 },
    { month: '12月', revenue: 2400000, profit: 480000, users: 240 },
  ];

  // 模拟角色分布数据
  const roleDistributionData = [
    { name: '用电用户', value: 120, color: '#1890ff', growth: 15.2 },
    { name: '一级推荐官', value: 45, color: '#52c41a', growth: 8.5 },
    { name: '二级推荐官', value: 35, color: '#faad14', growth: 12.3 },
    { name: '三级推荐官', value: 25, color: '#722ed1', growth: 6.7 },
  ];

  // 模拟地区分布数据
  const regionData = [
    { region: '华东', revenue: 850000, profit: 170000, users: 85 },
    { region: '华南', revenue: 680000, profit: 136000, users: 68 },
    { region: '华北', revenue: 520000, profit: 104000, users: 52 },
    { region: '西南', revenue: 320000, profit: 64000, users: 32 },
    { region: '东北', revenue: 280000, profit: 56000, users: 28 },
  ];

  // 模拟用户活跃度数据
  const userActivityData = [
    { date: '12-01', activeUsers: 120, newUsers: 5, totalUsage: 8500 },
    { date: '12-02', activeUsers: 125, newUsers: 3, totalUsage: 9200 },
    { date: '12-03', activeUsers: 118, newUsers: 7, totalUsage: 7800 },
    { date: '12-04', activeUsers: 132, newUsers: 4, totalUsage: 10500 },
    { date: '12-05', activeUsers: 128, newUsers: 6, totalUsage: 9800 },
    { date: '12-06', activeUsers: 135, newUsers: 8, totalUsage: 11200 },
    { date: '12-07', activeUsers: 140, newUsers: 5, totalUsage: 12000 },
  ];

  const regionColumns = [
    {
      title: '地区',
      dataIndex: 'region',
      key: 'region',
    },
    {
      title: '用户数',
      dataIndex: 'users',
      key: 'users',
      render: (users: number) => users.toLocaleString(),
    },
    {
      title: '收入（元）',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (revenue: number) => `¥${revenue.toLocaleString()}`,
    },
    {
      title: '利润（元）',
      dataIndex: 'profit',
      key: 'profit',
      render: (profit: number) => `¥${profit.toLocaleString()}`,
    },
    {
      title: '人均收入（元）',
      key: 'avgRevenue',
      render: (_: any, record: any) => 
        `¥${Math.round(record.revenue / record.users).toLocaleString()}`,
    },
  ];

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const handleExport = () => {
    // 模拟导出功能
    const dataStr = JSON.stringify({
      revenueTrend: revenueTrendData,
      roleDistribution: roleDistributionData,
      regionData: regionData,
      userActivity: userActivityData,
    }, null, 2);
    
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics_report_${dayjs().format('YYYY-MM-DD')}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col>
            <Space>
              <span>时间范围：</span>
              <Select
                value={timeRange}
                onChange={setTimeRange}
                style={{ width: 120 }}
              >
                <Option value="week">最近一周</Option>
                <Option value="month">最近一月</Option>
                <Option value="quarter">最近一季</Option>
                <Option value="year">最近一年</Option>
              </Select>
            </Space>
          </Col>
          <Col>
            <RangePicker
              placeholder={['开始日期', '结束日期']}
              onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            />
          </Col>
          <Col>
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                loading={loading}
                onClick={handleRefresh}
              >
                刷新
              </Button>
              <Button 
                icon={<DownloadOutlined />}
                onClick={handleExport}
              >
                导出报告
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <LineChartOutlined />
                收益趋势
              </Space>
            }
            className="dashboard-chart"
          >
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [
                    `¥${Number(value).toLocaleString()}`, 
                    name === 'revenue' ? '收入' : '利润'
                  ]}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#1890ff" 
                  strokeWidth={2}
                  name="收入"
                />
                <Line 
                  type="monotone" 
                  dataKey="profit" 
                  stroke="#52c41a" 
                  strokeWidth={2}
                  name="利润"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <PieChartOutlined />
                角色分布
              </Space>
            }
            className="dashboard-chart"
          >
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={roleDistributionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent, growth }) => (
                    <div>
                      <div>{name}</div>
                      <div>{(percent * 100).toFixed(1)}%</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        +{growth}%
                      </div>
                    </div>
                  )}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {roleDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
        <Col xs={24} lg={16}>
          <Card 
            title={
              <Space>
                <BarChartOutlined />
                地区分布
              </Space>
            }
            className="dashboard-chart"
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={regionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="region" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'revenue' ? `¥${Number(value).toLocaleString()}` : value,
                    name === 'revenue' ? '收入' : name === 'profit' ? '利润' : '用户数'
                  ]}
                />
                <Legend />
                <Bar dataKey="revenue" fill="#1890ff" name="收入" />
                <Bar dataKey="profit" fill="#52c41a" name="利润" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="关键指标" style={{ height: '300px' }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="总收入"
                  value={2400000}
                  precision={0}
                  valueStyle={{ color: '#1890ff' }}
                  prefix="¥"
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="总利润"
                  value={480000}
                  precision={0}
                  valueStyle={{ color: '#52c41a' }}
                  prefix="¥"
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="总用户"
                  value={233}
                  precision={0}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="月增长率"
                  value={12.5}
                  precision={1}
                  valueStyle={{ color: '#fa8c16' }}
                  suffix="%"
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <LineChartOutlined />
                用户活跃度
              </Space>
            }
            className="dashboard-chart"
          >
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={userActivityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="activeUsers" 
                  stroke="#1890ff" 
                  strokeWidth={2}
                  name="活跃用户"
                />
                <Line 
                  type="monotone" 
                  dataKey="newUsers" 
                  stroke="#52c41a" 
                  strokeWidth={2}
                  name="新增用户"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="地区详情">
            <Table
              columns={regionColumns}
              dataSource={regionData}
              pagination={false}
              size="small"
              scroll={{ y: 200 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DataAnalytics;
